import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useBeta } from '@/components/BetaProvider';
import { toast } from 'sonner';
import { 
  Send, 
  Bot, 
  User, 
  Sparkles, 
  Loader2, 
  Plus,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  FlaskConical,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Prompt templates for quick actions
const promptTemplates = {
  analyze: "Analyze my campaign performance and provide insights on how to improve CTR and conversions.",
  optimize: "How should I optimize my budget allocation across Google Ads and Meta for maximum ROAS?",
  creative: "Generate ad copy for a B2B SaaS product targeting marketing managers, focusing on lead generation.",
  forecast: "Forecast the performance of a $25,000 30-day campaign on Google Ads based on historical data."
};

export default function AIChat() {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [betaUsage, setBetaUsage] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { betaMode, refreshUsage } = useBeta();

  useEffect(() => {
    fetchSessions();
    fetchPendingTasks();
    if (betaMode) {
      fetchBetaUsage();
    }
    
    // Check for prompt query param
    const promptKey = searchParams.get('prompt');
    if (promptKey && promptTemplates[promptKey]) {
      setInput(promptTemplates[promptKey]);
      // Focus input
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [searchParams, betaMode]);

  const fetchBetaUsage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/beta/usage`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBetaUsage(data);
      }
    } catch (error) {
      console.error('Failed to fetch beta usage:', error);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const fetchPendingTasks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/pending`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending tasks:', error);
    }
  };

  const loadSession = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessionId(id);
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load chat session');
    }
  };

  const createNewSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setMessages([]);
        fetchSessions();
        toast.success('New conversation started');
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.error('Failed to start new conversation');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setIsTyping(true);

    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const response = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        if (response.status === 402) {
          toast.error('AI budget exceeded. Please add more balance via Profile -> Universal Key -> Add Balance.');
        } else if (response.status === 429) {
          toast.error(error.detail?.message || 'Daily AI limit reached. Try again tomorrow.');
          if (betaMode) fetchBetaUsage();
        } else {
          toast.error(error.detail?.message || 'Failed to send message');
        }
        setIsTyping(false);
        return;
      }

      const data = await response.json();
      
      // Update beta usage from response
      if (data.beta_usage) {
        setBetaUsage(data.beta_usage);
      }
      
      // Update session ID if new
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        fetchSessions();
      }

      // Simulate typing effect for response
      const fullResponse = data.response || '';
      setIsTyping(false);
      
      // Add assistant response with typing animation
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: fullResponse,
        timestamp: data.timestamp,
        metadata: {
          agent_used: data.agent_used,
          needs_approval: data.needs_approval,
          task_id: data.task_id
        }
      }]);

      // Refresh pending tasks if new approval needed
      if (data.needs_approval) {
        fetchPendingTasks();
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      setIsTyping(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskAction = async (taskId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          task_id: taskId,
          action: action
        })
      });

      if (response.ok) {
        toast.success(`Task ${action}d successfully`);
        fetchPendingTasks();
        
        if (action === 'approve') {
          const result = await response.json();
          // Add result to messages
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: result.response || 'Task completed successfully.',
            timestamp: new Date().toISOString(),
            metadata: { agent_used: result.agent_used }
          }]);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || `Failed to ${action} task`);
      }
    } catch (error) {
      console.error(`Failed to ${action} task:`, error);
      toast.error(`Failed to ${action} task`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getAgentBadge = (agent) => {
    const agentColors = {
      insight: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      creative: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      media: 'bg-green-500/20 text-green-400 border-green-500/30',
      orchestrator: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return agentColors[agent] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const examplePrompts = [
    "Analyze my Google Ads campaign performance",
    "Generate ad copy for a SaaS product targeting marketers",
    "How should I allocate a $50,000 budget across platforms?",
    "What optimization opportunities do I have?",
    "Forecast performance for a 30-day campaign"
  ];

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-80px)] gap-4" data-testid="ai-chat-page">
        {/* Sidebar - Sessions & Tasks */}
        <div className="w-72 flex flex-col gap-4">
          {/* New Chat Button */}
          <Button 
            onClick={createNewSession}
            className="w-full mk-btn-gold"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Conversation
          </Button>

          {/* Beta AI Usage */}
          {betaMode && betaUsage && (
            <Card className="mk-card" data-testid="beta-usage-card">
              <CardContent className="p-3">
                <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  AI Usage Today
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Requests</span>
                    <span className="text-foreground font-medium">
                      {betaUsage.used} / {betaUsage.limit}
                    </span>
                  </div>
                  <Progress 
                    value={(betaUsage.used / betaUsage.limit) * 100} 
                    className="h-2"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    {betaUsage.remaining > 0 
                      ? `${betaUsage.remaining} requests remaining`
                      : 'Limit reached. Resets at midnight UTC.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Tasks */}
          {pendingTasks.length > 0 && (
            <Card className="mk-card">
              <CardContent className="p-3">
                <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  Pending Approvals
                </h3>
                <div className="space-y-2">
                  {pendingTasks.slice(0, 3).map(task => (
                    <div 
                      key={task.task_id}
                      className="p-2 bg-slate-700/50 rounded-lg text-xs"
                    >
                      <p className="text-slate-300 truncate">{task.original_message}</p>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 px-2 text-green-400 hover:bg-green-500/20"
                          onClick={() => handleTaskAction(task.task_id, 'approve')}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 px-2 text-red-400 hover:bg-red-500/20"
                          onClick={() => handleTaskAction(task.task_id, 'reject')}
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Sessions */}
          <Card className="flex-1 mk-card overflow-hidden">
            <CardContent className="p-3">
              <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Recent Chats
              </h3>
              <ScrollArea className="h-[300px]">
                <div className="space-y-1">
                  {sessions.map(session => (
                    <button
                      key={session.session_id}
                      onClick={() => loadSession(session.session_id)}
                      className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                        sessionId === session.session_id 
                          ? 'bg-primary/20 text-primary' 
                          : 'hover:bg-secondary text-muted-foreground'
                      }`}
                    >
                      <p className="truncate">
                        {session.messages?.[0]?.content || 'New conversation'}
                      </p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">
                        {new Date(session.updated_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                  {sessions.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No conversations yet
                    </p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Main Chat Area */}
        <Card className="flex-1 mk-card flex flex-col">
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-primary-foreground" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    AI Assistant
                  </h2>
                  {betaMode && (
                    <Badge variant="outline" className="mb-3 mk-beta-badge">
                      <FlaskConical className="w-3 h-3 mr-1" />
                      Private Beta
                    </Badge>
                  )}
                  <p className="text-muted-foreground mb-6 max-w-md">
                    I can help you analyze campaigns, generate ad copy, optimize budgets, and more. Try one of these:
                  </p>
                  <div className="grid gap-2 w-full max-w-lg">
                    {examplePrompts.map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => setInput(prompt)}
                        className="p-3 text-left text-sm bg-secondary hover:bg-secondary/80 rounded-lg text-foreground transition-colors flex items-center gap-2"
                      >
                        <ChevronRight className="w-4 h-4 text-primary" />
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-primary-foreground" />
                        </div>
                      )}
                      <div className={`max-w-[70%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                        <div className={`p-3 rounded-lg ${
                          msg.role === 'user' 
                            ? 'mk-chat-user' 
                            : 'mk-chat-ai'
                        }`}>
                          <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                        </div>
                        {msg.metadata?.agent_used && (
                          <div className="mt-1 flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] ${getAgentBadge(msg.metadata.agent_used)}`}
                            >
                              {msg.metadata.agent_used} agent
                            </Badge>
                            {msg.metadata.needs_approval && (
                              <Badge variant="outline" className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30">
                                Awaiting Approval
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                        <Bot className="w-4 h-4 text-primary-foreground" />
                      </div>
                      <div className="mk-chat-ai p-3 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-xs text-muted-foreground">
                          {isTyping ? 'AI is thinking...' : 'Processing...'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about campaign analysis, ad copy, budget optimization..."
                  className="flex-1 bg-input border-border"
                  disabled={isLoading || (betaMode && betaUsage?.remaining === 0)}
                  data-testid="chat-input"
                />
                <Button 
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim() || (betaMode && betaUsage?.remaining === 0)}
                  className="mk-btn-gold"
                  data-testid="send-message-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              {betaMode && betaUsage?.remaining === 0 && (
                <p className="text-xs text-destructive mt-2">
                  Daily AI limit reached. Try again tomorrow after midnight UTC.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
